import {
  Slide,
  // eslint-disable-next-line @superdispatch/ui/no-restricted-modules
  Snackbar,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import {
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
  useResponsiveValue,
} from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useEffect, useMemo } from 'react';
import { useFlag } from 'shared/settings/FeatureToggles';
import { UploadIcon } from 'shared/icons/UploadIcon';
import styled from 'styled-components';
// eslint-disable-next-line @superdispatch/ui/no-restricted-modules
import { Alert } from '@material-ui/lab';
import { ButtonBase } from '@material-ui/core';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { LoadsAPI } from '../../data/LoadsAPI';
import { formatPlural } from 'shared/helpers/IntlHelpers';
import { CheckIcon } from 'shared/icons/CheckIcon';
import { Close } from 'shared/icons/Close';
import { FileListItem } from './FileListItem';
import { RejectedFileListItem } from './RejectedFileListItem';
import { FileError } from 'react-dropzone';
import { atom, useAtom } from 'jotai';
import { Prompt } from 'shared/routing/NavigationBlock';
import { useLoadsCache } from '../../data/LoadsAPI';
import { WarningTriangleIcon } from 'shared/icons/WarningTriangleIcon';

const AlertSnackbar = styled(Snackbar)`
  left: 100px;
  bottom: 0;
  min-width: 360px;
  max-width: 512px;
  padding: 0;

  & .MuiAlert-root {
    box-shadow:
      0 4px 8px 0 rgba(0, 0, 0, 0.4),
      0 0 4px 0 rgba(0, 0, 0, 0.3);

    @media (max-width: 960px) {
      width: 100vw;
    }
  }

  @media (max-width: 960px) {
    left: 0;
    bottom: 0;
    width: 100vw;
  }

  @media print {
    display: none;
  }
`;

const CollapseButton = styled(ButtonBase)<{ isReadyOnly: boolean }>`
  width: 100%;
  cursor: ${({ isReadyOnly }) => (isReadyOnly ? 'default' : 'pointer')};
`;

const ScrollableContainer = styled.div`
  max-height: 370px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 20px;
  padding-bottom: 10px;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${ColorDynamic.Dark100};
  }
`;

const WarningBox = styled(Box)`
  background-color: ${ColorDynamic.Yellow50};
  color: ${ColorDynamic.Yellow500};
`;

export interface RejectedImportFile {
  file: File;
  errors: readonly FileError[];
}

export const acceptedFilesAtom = atom<File[]>([]);
export const rejectedFilesAtom = atom<RejectedImportFile[]>([]);
export const isExpandedAtom = atom<boolean>(true);

const useStyle = makeStyles({
  root: {
    background: ColorDynamic.White,
    border: 'none',
    paddingRight: 0,
    paddingTop: '8px',
    paddingBottom: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
});

interface LoadsImportSnackbarProps {
  files: File[];
  onClose: () => void;
  rejectedFiles?: RejectedImportFile[];
  onRemoveRejectedFile?: (rejectedFile: File) => void;
}

export function LoadsImportSnackbar({
  files,
  onClose,
  rejectedFiles,
  onRemoveRejectedFile,
}: LoadsImportSnackbarProps) {
  const styles = useStyle();
  const queryClient = useQueryClient();
  const isMobile = useResponsiveValue(true, true, false);
  const shouldShowDropzone = useFlag('ctms_loads_dropzone');
  const [isExpanded, setExpand] = useAtom(isExpandedAtom);
  const loadsAPI = useMemo(() => new LoadsAPI(), []);
  const { invalidateLoads } = useLoadsCache();

  const filesToImport = useMemo(() => files.slice(0, 10), [files]);
  const ignoredFilesCount = files.length - filesToImport.length;

  const data = useQueries({
    queries: filesToImport.map((file) => ({
      queryKey: ['loads-import', file.name],
      queryFn: () => loadsAPI.uploadLoads(file),
      gcTime: Infinity,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: false,
    })),
  });

  const loadingCount = useMemo(() => {
    return (
      filesToImport.length - data.filter((query) => !query.isLoading).length
    );
  }, [filesToImport, data]);
  const guids = useMemo(() => {
    return data
      .flatMap((query) => query.data?.map((load) => load.guid) || [])
      .filter((guid: string) => !!guid);
  }, [data]);

  const cancelledLoadsCount = useMemo(
    () =>
      data.filter(
        (query) => query.fetchStatus === 'idle' && query.status === 'pending',
      ).length,
    [data],
  );

  const isLoaded = loadingCount === 0;
  const isLoading = loadingCount > 0;

  const errorCount =
    (rejectedFiles?.length || 0) + data.filter((query) => query.isError).length;
  const successCount = guids.length;
  const isOpen =
    shouldShowDropzone &&
    (errorCount > 0 || successCount > 0 || loadingCount > 0);

  const getStatusMessage = useMemo(() => {
    const parts: string[] = [];

    if (successCount > 0) {
      parts.push(
        `${successCount} ${formatPlural(successCount, 'load', 'loads')} imported`,
      );
    }

    if (errorCount > 0) {
      parts.push(`${errorCount} failed`);
    }

    if (cancelledLoadsCount > 0) {
      const cancelText =
        successCount === 0 && errorCount === 0
          ? 'Import'
          : cancelledLoadsCount.toString();
      parts.push(`${cancelText} canceled`);
    }

    return parts.join(' · ');
  }, [successCount, errorCount, cancelledLoadsCount]);

  useEffect(() => {
    if (isOpen && isLoaded) {
      invalidateLoads();
    }
  }, [isOpen, isLoaded, invalidateLoads]);

  return (
    <AlertSnackbar
      open={isOpen}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
    >
      <Alert
        icon={false}
        // eslint-disable-next-line react/forbid-component-props
        className={styles.root}
      >
        <Prompt
          when={isLoading}
          message="Changes have not been saved. Leaving can result in unsaved changes being lost."
        />
        <Slide direction="up" in={isOpen} timeout={300}>
          <Box width={isMobile ? '100vw' : '350px'}>
            <Stack space="xxsmall">
              <CollapseButton
                disableRipple={true}
                isReadyOnly={isLoaded && isExpanded}
                onClick={() => {
                  if (isLoaded && isExpanded) return;
                  setExpand(!isExpanded);
                }}
              >
                <Columns space="xsmall">
                  <Column width="content">
                    {isLoading || errorCount > 0 || cancelledLoadsCount > 0 ? (
                      <UploadIcon />
                    ) : (
                      <CheckIcon />
                    )}
                  </Column>
                  <Column width="fluid">
                    <Inline verticalAlign="center" space="xsmall">
                      {loadingCount > 0 && (
                        <Typography variant="h4" color="textPrimary">
                          Importing {loadingCount}{' '}
                          {formatPlural(loadingCount, 'file', 'files')}...
                        </Typography>
                      )}
                      {loadingCount === 0 && (
                        <Typography
                          variant="h4"
                          color="textPrimary"
                          align="left"
                        >
                          {getStatusMessage}
                        </Typography>
                      )}
                    </Inline>
                  </Column>
                  <Column width="content">
                    <Box paddingRight="small">
                      <ButtonBase
                        onClick={() => {
                          if (isLoaded && isExpanded) {
                            onClose();
                            queryClient.removeQueries({
                              queryKey: ['loads-import'],
                            });
                          } else {
                            setExpand(!isExpanded);
                          }
                        }}
                      >
                        {isLoaded && isExpanded ? (
                          <Close />
                        ) : isExpanded ? (
                          <ExpandMore />
                        ) : (
                          <ExpandLess />
                        )}
                      </ButtonBase>
                    </Box>
                  </Column>
                </Columns>
              </CollapseButton>

              {isExpanded && (
                <ScrollableContainer>
                  <Stack space="small">
                    {ignoredFilesCount > 0 && (
                      <Box width="100%" paddingRight="small">
                        <WarningBox width="100%" padding="xsmall">
                          <Columns align="top" space="xsmall">
                            <Column width="content">
                              <WarningTriangleIcon />
                            </Column>
                            <Column width="fluid">
                              <Typography variant="body2">
                                Too many files. We’ll import the first 10 and
                                ignore the rest.
                              </Typography>
                            </Column>
                          </Columns>
                        </WarningBox>
                      </Box>
                    )}
                    {data.map((result, index) => (
                      <FileListItem
                        key={index}
                        data={result}
                        file={filesToImport[index]}
                      />
                    ))}
                    {rejectedFiles?.map((file, index) => {
                      return (
                        <RejectedFileListItem
                          key={index}
                          rejectedFile={file}
                          onRemove={(rejectedFile) => {
                            onRemoveRejectedFile?.(rejectedFile);
                          }}
                        />
                      );
                    })}
                  </Stack>
                </ScrollableContainer>
              )}
            </Stack>
          </Box>
        </Slide>
      </Alert>
    </AlertSnackbar>
  );
}
